/**
 * TimelineHelpers - Utility functions for TimelineCalendar
 */
class TimelineHelpers {
  /**
   * Format a date to a string
   * @param {Date|string} date - Date to format
   * @param {string} format - Format string (e.g. 'y-m-d', 'y/m/d')
   * @param {number} yearOffset - Year offset (0 for current year)
   * @returns {string} Formatted date
   */
  static formatDate(date, format = 'd-m-y', yearOffset = 0) {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear() + yearOffset);

    return format
      .replace('d', day)
      .replace('m', month)
      .replace('y', year);
  }

  /**
   * Get localized month names
   * @param {string} locale - Locale string (e.g. 'th', 'en')
   * @param {string} format - 'long' for full names, 'short' for abbreviated
   * @returns {string[]} Array of month names
   */
  static getMonthNames(locale = 'th', format = 'long') {
    const months = [];
    const date = new Date(2000, 0, 1);

    for (let i = 0; i < 12; i++) {
      date.setMonth(i);
      months.push(
        date.toLocaleDateString(locale, {month: format})
      );
    }

    return months;
  }

  /**
   * Get localized day names
   * @param {string} locale - Locale string (e.g. 'th', 'en')
   * @param {string} format - 'long' for full names, 'short' for abbreviated
   * @returns {string[]} Array of day names
   */
  static getDayNames(locale = 'th', format = 'short') {
    const days = [];
    const date = new Date(2000, 0, 2);

    for (let i = 0; i < 7; i++) {
      date.setDate(date.getDate() + 1);
      days.push(
        date.toLocaleDateString(locale, {weekday: format})
      );
    }

    return days;
  }

  /**
   * Escape HTML special characters
   * @param {string} unsafe - String to escape
   * @returns {string} Escaped string
   */
  static escapeHtml(unsafe) {
    if (!unsafe) return '';
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /**
   * Save to localStorage with error handling
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @returns {boolean} Success status
   */
  static saveToStorage(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * Load from localStorage with error handling
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if not found
   * @returns {any} Retrieved value or default
   */
  static loadFromStorage(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Debounce function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  static debounce(func, wait = 300) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Throttle function
   * @param {Function} func - Function to throttle
   * @param {number} limit - Limit in milliseconds
   * @returns {Function} Throttled function
   */
  static throttle(func, limit = 300) {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall >= limit) {
        lastCall = now;
        func.apply(this, args);
      }
    };
  }

  /**
   * Update elements with data-i18n attribute
   * @param {HTMLElement} rootElement - Root element to start from
   * @param {TimelineCalendar} timeline - Timeline instance for translation
   */
  static translateElements(rootElement, timeline) {
    if (!timeline || !timeline.t) return;

    const elements = rootElement.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
      const key = element.getAttribute('data-i18n');
      const translation = timeline.t(key);

      if (translation) {
        if (element.hasAttribute('data-i18n-attr')) {
          const attr = element.getAttribute('data-i18n-attr');
          element.setAttribute(attr, translation);
        } else {
          element.textContent = translation;
        }
      }
    });
  }

  /**
   * Format a date in localized format
   * @param {Date} date - Date to format
   * @param {string} format - Format style ('short', 'medium', 'long', 'full')
   * @param {string} locale - Locale string (e.g. 'th', 'en')
   * @returns {string} Formatted date in local format
   */
  static formatLocalDate(date, format = 'medium', locale = 'th') {
    try {
      const options = {dateStyle: format};
      return new Date(date).toLocaleDateString(locale, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return this.formatDate(date, 'd/m/y');
    }
  }

  /**
   * Get day difference between two dates
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @returns {number} Difference in days
   */
  static dateDiffInDays(startDate, endDate) {
    const _MS_PER_DAY = 86400000;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

    return Math.floor((utcEnd - utcStart) / _MS_PER_DAY);
  }

  /**
   * Check if a date is a weekend
   * @param {Date|string} date - Date to check
   * @returns {boolean} True if date is a weekend
   */
  static isWeekend(date) {
    const d = new Date(date);
    const day = d.getDay();
    return day === 0 || day === 6;
  }

  /**
   * Create date range array between two dates
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @param {string} format - Date format for result
   * @returns {string[]} Array of dates in specified format
   */
  static getDateRange(startDate, endDate, format = 'y-m-d') {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dateArray = [];
    let currentDate = new Date(start);

    while (currentDate <= end) {
      dateArray.push(this.formatDate(new Date(currentDate), format));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateArray;
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = TimelineHelpers;
}

window.TimelineHelpers = TimelineHelpers;
